import { Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { EMPTY, forkJoin, Observable, of, switchMap } from 'rxjs';
import { ELoadStatus } from '../../../state/state.interface';
import { UserEffects } from '../../../state/user/user.effects';
import { EAuthProvider } from '../../../state/user/user.interface';
import { IDropdownOption } from '../../components/mat-dropdown/mat-dropdown.component';
import { IOeeCurrentShift, IOeeShift } from './oee-tracker.interface';
import { OeeTrackerService } from './oee-tracker.service';

export interface ILoadLineShiftsRequest {
  lineId: number;
  withCurrentShift: boolean;
}

export interface IOeeTrackerState {
  currentShift: IOeeCurrentShift | null;
  currentShiftLoading: ELoadStatus;
  lineOptions: IDropdownOption[];
  lineOptionsLoading: ELoadStatus;
  shifts: IOeeShift[];
  shiftsLoading: ELoadStatus;
}

@Injectable()
export class OeeTrackerStore extends ComponentStore<IOeeTrackerState> {
  public readonly isAvailable: boolean =
    Boolean(UserEffects.getUser().moduleUrls.oeeTrackerBackendUrl) &&
    UserEffects.getUser().authProvider === EAuthProvider.digitalFactory;

  public readonly lineOptions: Signal<IDropdownOption[]> = this.selectSignal((state) => state.lineOptions);
  public readonly lineOptionsLoading$: Observable<ELoadStatus> = this.select((state) => state.lineOptionsLoading);
  public readonly shifts: Signal<IOeeShift[]> = this.selectSignal((state) => state.shifts);
  public readonly shiftsLoading$: Observable<ELoadStatus> = this.select((state) => state.shiftsLoading);
  public readonly currentShift: Signal<IOeeCurrentShift | null> = this.selectSignal((state) => state.currentShift);

  readonly loadLines = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() =>
        this.withAvailabilityGuard(() => {
          this.patchState({ lineOptionsLoading: ELoadStatus.loading });

          return this.oeeTrackerService.getLines().pipe(
            tapResponse(
              (response) =>
                this.patchState({
                  lineOptions: response.data.map((line) => ({ id: line.id, name: line.title })),
                  lineOptionsLoading: ELoadStatus.success,
                }),
              // eslint-disable-next-line no-console
              (error) => console.error('Error loading OEE lines', error),
            ),
          );
        }),
      ),
    ),
  );

  readonly loadLineShifts = this.effect((trigger$: Observable<ILoadLineShiftsRequest>) =>
    trigger$.pipe(
      switchMap(({ lineId, withCurrentShift }: ILoadLineShiftsRequest) =>
        this.withAvailabilityGuard(() => {
          this.patchState({ currentShiftLoading: ELoadStatus.loading, shiftsLoading: ELoadStatus.loading });

          return forkJoin({
            currentShifts: withCurrentShift ? this.oeeTrackerService.getCurrentShifts(lineId) : of(null),
            shifts: this.oeeTrackerService.getShifts([lineId]),
          }).pipe(
            tapResponse(
              ({ currentShifts, shifts }) =>
                this.patchState({
                  currentShift: currentShifts?.data?.find((shift) => shift.lineId === lineId) ?? null,
                  currentShiftLoading: ELoadStatus.success,
                  shifts: shifts.data,
                  shiftsLoading: ELoadStatus.success,
                }),
              // eslint-disable-next-line no-console
              (error) => console.error('Error loading OEE line shifts', error),
            ),
          );
        }),
      ),
    ),
  );

  constructor(private readonly oeeTrackerService: OeeTrackerService) {
    super({
      currentShift: null,
      currentShiftLoading: ELoadStatus.initial,
      lineOptions: [],
      lineOptionsLoading: ELoadStatus.initial,
      shifts: [],
      shiftsLoading: ELoadStatus.initial,
    });
  }

  private withAvailabilityGuard<T>(operation: () => Observable<T>): Observable<T> {
    if (!this.isAvailable) {
      // eslint-disable-next-line no-console
      console.error('OEE Tracker is not available: missing backend URL or incorrect auth provider');

      return EMPTY;
    }

    return operation();
  }
}
